import {
    AuthenticationState,
    UserContext,
} from "@contexts/UserDataProvider/UserReducer"
import {
    AnalyticsActionType,
    AnalyticsContext,
    DrawerActionType,
    DrawerContext,
    PollActionType,
    PollContext,
    ScreenShotActionType,
    ScreenShotContext,
} from "@contexts"
import { ReactNode, useCallback, useContext } from "react"
import { t } from "i18next"
import { LogEntry, updatePoll } from "@services/poll/updatePoll"
import { PollAction } from "@contexts/PollContext"
import Logger from "@services/logger"
import { ThemeContext } from "@contexts/ThemeContext"
import { Vote } from "@contexts/PollContext/PollReducer"
import { AnalyticsEvent } from "@services/analytics"
import { ModalName } from "@components/Drawer/content"
import { PollSettings } from "@types"
import { isPlatformNative } from "@helpers/isPlatformNative"
import { useLogAnalytic } from "@helpers/hooks/useAnalyticsActions"

interface callbackProps {
    pollId: string
    sequentialUpdates: LogEntry[]
    resultingVotes: Vote[]
}

interface useUpdatePollFlowProp {
    pollDispatch: PollAction
    drawerContent: {
        shareVote: ReactNode
        createOwnPoll: ReactNode
    }
}

export const useUpdatePollFlow = ({
    pollDispatch,
    drawerContent,
}: useUpdatePollFlowProp) => {
    const {
        userState: { deviceId, authStatus, userName },
    } = useContext(UserContext)
    const { drawerDispatch } = useContext(DrawerContext)
    const { screenShotDispatch } = useContext(ScreenShotContext)
    const { theme } = useContext(ThemeContext)
    const {
        pollState: { originalOptionsLength },
    } = useContext(PollContext)

    const isNative = isPlatformNative()
    const logAnalytic = useLogAnalytic()

    const refreshPollData = useCallback(() => {
        pollDispatch({ type: PollActionType.REFRESH_POLL_DATA })
    }, [pollDispatch])

    const clearShareLink = useCallback(() => {
        screenShotDispatch({
            type: ScreenShotActionType.SET_SHARE_LINK,
            payload: {
                shareLink: "",
            },
        })
    }, [screenShotDispatch])

    const onUpdateSuccessDismiss = useCallback(
        (postVoteAction: PollSettings["postVoteAction"]) => {
            switch (postVoteAction) {
                case "shareVote":
                    drawerDispatch({
                        type: DrawerActionType.SHOW_DRAWER,
                        payload: {
                            content: drawerContent.shareVote,
                            dismissible: true,
                            hasCloseButton: true,
                            hasBackground: true,
                            onDismiss: clearShareLink,
                            identifier: ModalName.SHARE_VOTE,
                        },
                    })
                    break
                case "downloadPollsApp":
                    if (isNative) break

                    drawerDispatch({
                        type: DrawerActionType.SHOW_DRAWER,
                        payload: {
                            content: drawerContent.createOwnPoll,
                            hasBackground: true,
                            dismissible: true,
                            hasCloseButton: true,
                            isModal: true,
                            identifier: ModalName.POST_VOTE_DOWNLOAD_POLLS,
                        },
                    })
                    break
            }
        },
        [],
    )

    return useCallback(
        async ({
            pollId,
            sequentialUpdates,
            resultingVotes,
        }: callbackProps) => {
            Logger.info("FLOW: updatePoll initialized")

            const { isSuccess, data, error } = await updatePoll({
                userName,
                pollId,
                deviceId,
                sequentialUpdates,
                resultingVotes,
                isAuthenticated:
                    authStatus === AuthenticationState.AUTHENTICATED,
            })

            if (isSuccess && data) {
                const handleUpdateSuccessDismiss = () =>
                    onUpdateSuccessDismiss(data.settings.postVoteAction)

                const event = sequentialUpdates.length
                    ? AnalyticsEvent.updatePollSuccess
                    : AnalyticsEvent.voteSuccess

                logAnalytic(event)

                pollDispatch({
                    type: PollActionType.SET_DATA,
                    payload: { data, pollTitle: "" },
                })

                drawerDispatch({
                    type: DrawerActionType.SHOW_SUCCESS_MODAL,
                    payload: {
                        title: t("youVoted"),
                        message: t("shareWithFriends"),
                        animation: theme.lottie.voteSuccess,
                        duration: 1500,
                        onDismiss: handleUpdateSuccessDismiss,
                    },
                })

                pollDispatch({
                    type: PollActionType.CREATE_PREVIEW_IMAGE,
                })
            } else if (error) {
                if (error.response?.status === 409) {
                    drawerDispatch({
                        type: DrawerActionType.SHOW_MODAL,
                        payload: {
                            title: t("updateFailed"),
                            message: t("bottomNavigation.someoneElseChanged"),
                            image: theme.image.updatePollCrossErrorGraphic,
                            imageSize: {
                                height: 89,
                                width: 248,
                            },
                            buttonTitle: t("reloadPoll"),
                            onDismiss: refreshPollData,
                            dismissible: false,
                            identifier: ModalName.WIRES_CROSSED,
                        },
                    })
                } else {
                    drawerDispatch({
                        type: DrawerActionType.SHOW_MODAL,
                        payload: {
                            title: t("updateFailed"),
                            message:
                                t("bottomNavigation.couldntUpdateThePoll") +
                                " " +
                                t("bottomNavigation.checkConnection"),
                            image: theme.image.updatePollFailedGraphic,
                            imageSize: {
                                height: 105,
                                width: 131,
                            },
                            buttonTitle: t("okay"),
                            identifier: ModalName.FAILED_TO_SUBMIT_VOTE,
                        },
                    })
                }
            }

            pollDispatch({
                type: PollActionType.POLL_DATA_UPDATED,
            })
        },
        [
            authStatus,
            deviceId,
            drawerDispatch,
            userName,
            pollDispatch,
            refreshPollData,
            screenShotDispatch,
            originalOptionsLength,
            t,
        ],
    )
}
